import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import api from "@/lib/api";
import { MapPin, Navigation, Phone, Mail } from "lucide-react";

const toLngLat = (c) => {
  if (!c) return null;
  if (Array.isArray(c) && c.length >= 2) return [Number(c[0]), Number(c[1])];
  if (typeof c === "object") {
    const lng = c.lng ?? c.longitude ?? c.lon;
    const lat = c.lat ?? c.latitude;
    if (lng != null && lat != null) return [Number(lng), Number(lat)];
  }
  return null;
};

function VenueGallery({ venue }) {
  const media = [...(venue?.photos || []), ...(venue?.plans || [])];
  if (!media.length) return null;
  return (
    <div className="mt-4" data-testid="ev-venue-media">
      <p className="mb-2 text-sm font-semibold text-foreground">Photos & plans du lieu{venue?.name ? ` — ${venue.name}` : ""}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {media.map((m, i) => {
          const src = typeof m === "string" ? m : (m.url || m.image || m.data || "");
          if (!src) return null;
          return (
            <a key={i} href={src} target="_blank" rel="noreferrer" data-testid={`ev-venue-media-${i}`} className="block overflow-hidden rounded-xl border border-border transition-transform hover:scale-[1.02]">
              <img src={src} alt={m.name || `Média ${i + 1}`} className="h-36 w-full object-cover" />
            </a>
          );
        })}
      </div>
    </div>
  );
}

function VenueContact({ venue }) {
  const vphone = venue?.contact_phone || venue?.phone;
  const vemail = venue?.email || venue?.contact_email;
  if (!vphone && !vemail) return null;
  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="ev-venue-contact">
      <p className="text-sm font-semibold text-foreground">Contacter le lieu{venue?.name ? ` — ${venue.name}` : ""}{venue?.contact_name ? ` · ${venue.contact_name}` : ""}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {vphone && <a href={`tel:${vphone}`} data-testid="ev-venue-call" className="inline-flex items-center gap-1.5 rounded-full bg-[#FF6A00] px-3.5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#E65F00]"><Phone className="h-4 w-4" />Appeler {vphone}</a>}
        {vemail && <a href={`mailto:${vemail}`} data-testid="ev-venue-email" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-sm text-foreground transition-colors hover:bg-muted"><Mail className="h-4 w-4" />Envoyer un email</a>}
      </div>
    </div>
  );
}

function useSatelliteMap(ref, lngLat, coords) {
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!lngLat || !ref.current) return;
    let map;
    (async () => {
      try {
        const { data } = await api.get("/config/mapbox");
        if (!data.token) { setErr("Mapbox non configuré."); return; }
        mapboxgl.accessToken = data.token;
        map = new mapboxgl.Map({ container: ref.current, style: "mapbox://styles/mapbox/satellite-streets-v12", center: lngLat, zoom: 16 });
        map.addControl(new mapboxgl.NavigationControl(), "top-right");
        map.addControl(new mapboxgl.FullscreenControl(), "top-right");
        new mapboxgl.Marker({ color: "#FF6A00" }).setLngLat(lngLat).addTo(map);
      } catch { setErr("Carte indisponible."); }
    })();
    return () => { if (map) map.remove(); };
  }, [coords]); // eslint-disable-line
  return err;
}

export default function EventMap({ coords, address, venue }) {
  const ref = useRef(null);
  const lngLat = toLngLat(coords);
  const err = useSatelliteMap(ref, lngLat, coords);
  return (
    <div>
      {lngLat ? (
        <>
          <div ref={ref} data-testid="ev-map" className="h-[460px] w-full overflow-hidden rounded-2xl border border-border" />
          {err && <p className="mt-2 text-sm text-red-500">{err}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {address && <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4" />{address}</span>}
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${lngLat[1]},${lngLat[0]}`} target="_blank" rel="noreferrer" data-testid="ev-map-directions" className="inline-flex items-center gap-1.5 rounded-full bg-[#FF6A00] px-3.5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#E65F00]"><Navigation className="h-4 w-4" />Itinéraire</a>
          </div>
        </>
      ) : (
        <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="ev-map-nocoords">
          <MapPin className="mx-auto h-9 w-9 text-[#FF6A00]" />
          <p className="mt-3 font-display text-lg font-semibold text-foreground">Lieu non géolocalisé</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{address || "Aucune adresse renseignée."}</p>
          <p className="mt-2 text-xs text-muted-foreground">Renseignez l'adresse du lieu (avec l'autocomplétion) pour afficher la carte satellite.</p>
        </div>
      )}
      <VenueGallery venue={venue} />
      <VenueContact venue={venue} />
    </div>
  );
}
